import React, { useState, useMemo, useContext } from 'react';
import { ComingSoonData, ComingSoonWeek } from '@/api/coming-soon';
import { MetadataContext, UserContext } from '@/components/ContextProviders';

import ComingSoonReport from './ComingSoonReport';
import ComingSoonSelectedReports from './ComingSoonSelectedReports';
import DropdownFilter from '@/components/search/DropdownFilter';
import { useComingSoon } from './useComingSoon';

interface ComingSoonListingProps {
  data: ComingSoonData;
  userId?: string;
  culture?: string;
}

interface TabItem {
  tabName: string;
  reports: ComingSoonWeek['reports'];
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const ComingSoonListing: React.FC<ComingSoonListingProps> = ({ data, userId, culture }) => {
  const metadata = useContext(MetadataContext);
  const user = useContext(UserContext);

  const finalUserId = userId || user.userId;
  const finalCulture = culture || metadata.culture;
  const [activeTab, setActiveTab] = useState(0);

  const { reports, selectedDirectories, loading, directoryOptions, handleSubscriptionChange, handleDirectoryFilter } =
    useComingSoon({ initialData: data, userId: finalUserId, culture: finalCulture });

  const tabs: TabItem[] = useMemo(() => {
    const tabsData = reports.map((week) => {
      const day = String(new Date(week.firstDayOfWeek).getUTCDate());
      const ordinalSuffix = getOrdinalSuffix(parseInt(day, 10));
      return {
        tabName: `${week.month} w/c ${day}${ordinalSuffix}`,
        reports: week.reports,
      };
    });
    return tabsData;
  }, [reports]);

  const handleDirectoryFilterWithTabReset = async (directories: string[]) => {
    await handleDirectoryFilter(directories);
    setActiveTab(0);
  };

  const onDirectoryChange = (selectedValue: string) => {
    const newSelected = selectedDirectories.includes(selectedValue)
      ? selectedDirectories.filter((d) => d !== selectedValue)
      : [...selectedDirectories, selectedValue];
    handleDirectoryFilterWithTabReset(newSelected);
  };

  const onDirectoryClear = () => {
    handleDirectoryFilterWithTabReset([]);
  };

  return (
    <div className="container mx-auto my-3 px-3 sm:px-4 md:px-6 lg:mx-auto lg:max-w-[1400px] xl:max-w-[1600px]">
      <div className="mb-4">
        <div className="rounded bg-white">
          <div className="p-4 sm:p-5 md:p-6">
            <div className="mb-4 w-full sm:mb-6">
              <div className="max-w-[300px] opacity-100 lg:max-w-[500px]">
                <DropdownFilter
                  name="Filter by Directory"
                  options={directoryOptions}
                  value={selectedDirectories}
                  onChange={onDirectoryChange}
                  onClear={onDirectoryClear}
                  disabled={false}
                  className="[&_.content]:w-full [&_.content-wrapper]:w-full"
                />
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-4">
              <div className="relative flex w-full justify-between border-b border-[#ccc]">
                {tabs.map((tab, index) => (
                  <button
                    key={tab.tabName}
                    onClick={() => setActiveTab(index)}
                    className={`relative flex-1 cursor-pointer border-b border-[#ccc] bg-transparent px-2 py-3 text-center text-[16px] leading-[20px] font-semibold tracking-[0.1px] capitalize transition-all duration-200 outline-none focus:outline-none active:scale-[0.98] lg:text-[18px] lg:leading-[22px] ${
                      activeTab === index ? 'border-b-black !bg-[#e0e0e0]' : 'hover:bg-[#f5f5f5]'
                    }`}
                    style={{ fontFamily: 'Sohne-Semi-Bold, sans-serif', WebkitTapHighlightColor: 'transparent' }}
                  >
                    {tab.tabName}
                  </button>
                ))}
                <div
                  className="absolute bottom-0 z-[1] h-[1px] bg-black transition-all duration-300 ease-in-out"
                  style={{
                    left: `${(activeTab / tabs.length) * 100}%`,
                    width: `${100 / tabs.length}%`,
                  }}
                />
              </div>
            </div>

            {/* Tab Content */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-black"></div>
              </div>
            ) : tabs.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-gray-500">No coming soon data available.</p>
              </div>
            ) : tabs[activeTab]?.reports.length > 0 ? (
              <div className="space-y-0">
                {tabs[activeTab].reports.map((report) => (
                  <ComingSoonReport
                    key={report.reportId}
                    report={report}
                    onSubscriptionChange={handleSubscriptionChange}
                  />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">No reports available for the selected period.</p>
              </div>
            )}
          </div>

          <ComingSoonSelectedReports
            tabs={tabs}
            onUnsubscribe={(reportId) => handleSubscriptionChange(reportId, false)}
          />
        </div>
      </div>
    </div>
  );
};

export default ComingSoonListing;
